import { useAuth } from "@/hooks/use-auth";
import { useCustomer } from "@/hooks/use-customer";
import { LanguageSelector } from "@/components/language-selector";
import { UserMenu } from "@/components/user-menu";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { 
  Menu, Users, Home, PlusCircle, FileText, ClipboardList, Settings, KeyRound, ArrowLeft, Laptop, FileSpreadsheet, Building2
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";

interface HeaderProps {
  onToggleSidebar?: () => void;
}

export function Header({ onToggleSidebar }: HeaderProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const { activeCustomerId, setActiveCustomerId, activeCustomer, customers, isLoading } = useCustomer();

  // Query to find the active proposal for this facility
  const { data: osemProposals = [] } = useQuery<any[]>({
    queryKey: [`/api/osem?customerId=${activeCustomerId}`],
    enabled: !!activeCustomerId,
  });
  const proposalsList = Array.isArray(osemProposals) ? osemProposals : [];
  const activeProposal = proposalsList[0] || null;
  const proposalId = activeProposal?.id;
  const hasApprovedProposal = activeProposal?.status === "approved";
  
  const hasMenuAccess = (path: string) => {
    if (user?.role === "Admin" || user?.role === "User" || user?.role === "Viewer") return true;
    if (!(user as any)?.visibleMenus) return path === "/osem-onboarding"; // Default fallback
    return (user as any).visibleMenus.includes(path);
  };

  const NavItem = ({ to, icon, label, isActive }: { to: string, icon: React.ReactNode, label: string, isActive: boolean }) => (
    <Link href={to}>
      <a className={cn(
        "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors",
        isActive 
          ? "bg-primary text-primary-foreground" 
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}>
        {icon}
        <span className="hidden sm:inline">{label}</span>
      </a>
    </Link>
  );

  return (
    <header className="bg-background border-b w-full flex flex-col z-50 fixed top-0 left-0">
      {/* Top Bar: Logo & Actions */}
      <div className="flex justify-between items-center h-16 px-4 md:px-6">
        <div className="flex items-center gap-3">
          <a
            href="https://app.onitnetworking.com/eofdd/"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted"
            title="Back to Energy Intelligence"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Energy Intelligence</span>
          </a>
          <span className="text-2xl font-bold text-primary">
            Pump ECM
          </span>
        </div>

        <div className="flex items-center space-x-2 md:space-x-4">
          
          {!isLoading && customers.length > 0 && (
            <div className="hidden md:flex items-center space-x-2 mr-2">
              <span className="text-sm font-medium text-muted-foreground">
                {user?.role === "Admin" ? "Admin:" : "Facility:"}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                className="hover:bg-primary/10 transition-colors"
                onClick={() => {
                  setActiveCustomerId(null);
                  setLocation("/");
                }}
              >
                <Users className="mr-2 h-4 w-4" />
                {activeCustomer ? activeCustomer.username : "Select Facility"}
              </Button>
            </div>
          )}

          <a href="http://localhost:5002/">
            <Button variant="secondary" size="sm" className="hidden lg:flex items-center space-x-2">
              <span>Weather / TMY3</span>
            </Button>
          </a>
          
          <LanguageSelector />
          {user && <UserMenu user={user} />}
        </div>
      </div>
      <div className="border-b bg-muted/20">
        <nav className="container flex h-12 items-center space-x-4 px-4 overflow-x-auto">
          <NavItem
            to="/"
            icon={<Building2 className="h-4 w-4" />}
            label="Facilities"
            isActive={location === "/"}
          />
          <NavItem
            to="/osem-proposals"
            icon={<ClipboardList className="h-4 w-4" />}
            label="Proposals"
            isActive={location === "/osem-proposals" || location === "/osem-onboarding"}
          />

          {activeCustomer !== null ? (
            <>
              <div className="w-px h-6 bg-border mx-2" />
              <NavItem 
                to={proposalId ? `/osem-onboarding/${proposalId}` : "/osem-onboarding"}
                icon={<ClipboardList className="h-4 w-4" />} 
                label="Onboarding Form" 
                isActive={location.startsWith("/osem-onboarding/") && !location.endsWith("/emp") && !location.endsWith("/lighting") && !location.endsWith("/glidepath")}
              />

              <NavItem 
                to={proposalId ? `/osem-onboarding/${proposalId}/emp` : "/osem-onboarding"}
                icon={<FileText className="h-4 w-4" />} 
                label="Energy Management Plan" 
                isActive={location.endsWith("/emp")}
              />

              {hasApprovedProposal && (
                <NavItem 
                  to="/nyserda" 
                  icon={<ClipboardList className="h-4 w-4" />} 
                  label="ECM Summary" 
                  isActive={location === "/nyserda"}
                />
              )}
            </>
          ) : null}
          
          {user?.role === "Admin" && (
            <>
              <div className="w-px h-6 bg-border mx-2" />
              <NavItem
                to="/settings/system"
                icon={<Settings className="h-4 w-4" />}
                label="System"
                isActive={location === "/settings/system"}
              />
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
